import { useTheme } from '../ui/ThemeProvider';

interface AttemptEntry {
  digits: number[];
  correct_placed: number;
  correct_misplaced: number;
  solved: boolean;
}

interface AttemptLogProps {
  attempts: AttemptEntry[];
}

export function AttemptLog({ attempts }: AttemptLogProps) {
  const { theme } = useTheme();

  if (attempts.length === 0) {
    return (
      <div
        className="attempt-log attempt-log--empty"
        style={{
          padding: theme.spacing.lg,
          textAlign: 'center',
          color: theme.colours.text.tertiary,
          fontSize: theme.typography.fontSize.sm,
          fontFamily: theme.typography.fontFamily,
        }}
      >
        No attempts yet. Enter your first guess.
      </div>
    );
  }

  return (
    <div
      className="attempt-log"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.sm,
        maxHeight: '400px',
        overflowY: 'auto',
        padding: theme.spacing.md,
        backgroundColor: theme.colours.background.secondary,
        borderRadius: theme.borders.radius.md,
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {attempts.map((attempt, index) => {
        const entryColour = attempt.solved ? theme.colours.accent.success : theme.colours.text.primary;

        return (
          <div
            key={index}
            className="attempt-log__entry"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.md,
              padding: theme.spacing.sm,
              backgroundColor: attempt.solved
                ? `${theme.colours.accent.success}20`
                : theme.colours.background.tertiary,
              borderRadius: theme.borders.radius.sm,
              border: attempt.solved ? `2px solid ${theme.colours.accent.success}` : 'none',
            }}
          >
            <span
              className="attempt-log__number"
              style={{
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colours.text.tertiary,
                minWidth: '2rem',
              }}
            >
              #{index + 1}
            </span>

            <div
              className="attempt-log__digits"
              style={{
                display: 'flex',
                gap: theme.spacing.xs,
                flex: 1,
              }}
            >
              {attempt.digits.map((digit, i) => (
                <span
                  key={i}
                  style={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: theme.typography.fontSize.md,
                    fontWeight: theme.typography.fontWeight.bold,
                    color: entryColour,
                    letterSpacing: '0.1em',
                  }}
                >
                  {digit}
                </span>
              ))}
            </div>

            <div
              className="attempt-log__feedback"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
              }}
            >
              {attempt.correct_placed > 0 && (
                <span
                  style={{
                    color: theme.colours.feedback.placed,
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.xs,
                  }}
                >
                  <span>●</span>
                  <span>{attempt.correct_placed}</span>
                </span>
              )}

              {attempt.correct_misplaced > 0 && (
                <span
                  style={{
                    color: theme.colours.feedback.misplaced,
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.xs,
                  }}
                >
                  <span>○</span>
                  <span>{attempt.correct_misplaced}</span>
                </span>
              )}

              {attempt.correct_placed === 0 && attempt.correct_misplaced === 0 && (
                <span
                  style={{
                    color: theme.colours.feedback.incorrect,
                    fontSize: theme.typography.fontSize.xs,
                  }}
                >
                  ✗
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
